import { STAGE } from './stage.js';
import { MODEL_DISTANCE, modelSweptBox, turnTowards, } from './models.js';
import { shortestTurn } from './model-motion.js';
/**
 * Whether a model is small (spec table): its box, at the size the lesson
 * shows it, at most 2.5 m high and 3 m long -- a skeleton, a globe. A small
 * model stands on the stage like a shape and keeps 1.5 m from the eyes.
 */
export function isSmallModel(facts, scale = 1) {
    return (facts.box.height * scale <= MODEL_DISTANCE.smallHeight + 1e-9 &&
        facts.box.length * scale <= MODEL_DISTANCE.smallLength + 1e-9);
}
/**
 * Whether a model is a static exhibit (ADR-043; docs/04, "The distance
 * rule"): the library gives it no animations at all -- a skeleton, a statue.
 * It cannot walk (a walk needs a gait, and a gait an animation), play or
 * breathe, so it never comes at anyone: like a shape, it may stand beside
 * the pupil at any size, 1.5 m from the eyes, and it never veils the
 * lectern. A `face` can still turn it, and every turn is checked. Pieter
 * wanted the triceratops skeleton life-size beside the class ("the wow
 * factor", 5 Oct 2026); the big-model rule would have sent it 8.5 m off.
 */
export function isStaticModel(facts) {
    return Object.keys(facts.animations).length === 0;
}
/** Small first (a small static model is small); then a static exhibit; else big. */
export function modelStanding(facts, scale = 1) {
    if (isSmallModel(facts, scale))
        return 'small';
    return isStaticModel(facts) ? 'exhibit' : 'big';
}
/**
 * A model's footprint standing at `pos` turned `turn`: its box -- swept by
 * the clips it plays -- scaled, turned and moved there, with 0.3 m all round.
 */
export function modelFootprint(facts, pos, turn, options = {}) {
    const scale = options.scale ?? 1;
    const box = modelSweptBox(facts, options.clips);
    const m = MODEL_DISTANCE.margin;
    const [x0, x1] = [box.min[0] * scale - m, box.max[0] * scale + m];
    const [z0, z1] = [box.min[2] * scale - m, box.max[2] * scale + m];
    const radians = (turn * Math.PI) / 180;
    const c = Math.cos(radians);
    const s = Math.sin(radians);
    // About y as three.js turns rotation.y: (x, z) → (x cos + z sin, −x sin + z cos).
    const place = ([x, z]) => [pos[0] + x * c + z * s, pos[1] - x * s + z * c];
    return [place([x0, z0]), place([x1, z0]), place([x1, z1]), place([x0, z1])];
}
/** How far a point is from a footprint, horizontally; 0 inside it. */
export function footprintDistance(footprint, point) {
    if (isInside(footprint, point))
        return 0;
    let nearest = Infinity;
    for (let i = 0; i < footprint.length; i++) {
        const a = footprint[i];
        const b = footprint[(i + 1) % footprint.length];
        const [ex, ez] = [b[0] - a[0], b[1] - a[1]];
        const [px, pz] = [point[0] - a[0], point[1] - a[1]];
        const t = Math.max(0, Math.min(1, (px * ex + pz * ez) / (ex * ex + ez * ez || 1)));
        nearest = Math.min(nearest, Math.hypot(px - ex * t, pz - ez * t));
    }
    return nearest;
}
/** Whether a point is in a convex footprint (on an edge counts), whichever way its corners go round. */
function isInside(footprint, point) {
    let sign = 0;
    for (let i = 0; i < footprint.length; i++) {
        const a = footprint[i];
        const b = footprint[(i + 1) % footprint.length];
        const cross = (b[0] - a[0]) * (point[1] - a[1]) - (b[1] - a[1]) * (point[0] - a[0]);
        if (Math.abs(cross) < 1e-12)
            continue;
        if (sign === 0)
            sign = Math.sign(cross);
        else if (Math.sign(cross) !== sign)
            return false;
    }
    return true;
}
/**
 * How far a model's front reaches ahead of where it stands: the most forward
 * point of what it sweeps, scaled, plus the margin. What the clamp keeps 2.5 m
 * from the eyes.
 */
export function modelFront(facts, options = {}) {
    const box = modelSweptBox(facts, options.clips);
    return -box.min[2] * (options.scale ?? 1) + MODEL_DISTANCE.margin;
}
/** How near the eyes a model's front may come when it walks to the pupil: 2.5 m, a small model 1.5 m. */
export function modelStopDistance(facts, scale = 1) {
    return isSmallModel(facts, scale) ? MODEL_DISTANCE.smallEyes : MODEL_DISTANCE.spannendEyes;
}
/**
 * The clamp (spec "The distance rule"): where a model walking from `from`
 * straight at the pupil's eyes at `pupil` stops, so its front -- with what
 * the step's clips sweep, a bite's reach included -- ends 2.5 m (a small
 * model 1.5 m) from the eyes. One already nearer stays where it is. The
 * engine stops the walk here whatever the lesson says.
 */
export function modelStopPoint(from, pupil, facts, options = {}) {
    const keep = modelStopDistance(facts, options.scale ?? 1) + modelFront(facts, options);
    const distance = Math.hypot(pupil[0] - from[0], pupil[1] - from[1]);
    if (distance <= keep + 1e-9)
        return [from[0], from[1]];
    const k = (distance - keep) / distance;
    return [from[0] + (pupil[0] - from[0]) * k, from[1] + (pupil[1] - from[1]) * k];
}
/** The turn a walk to the pupil walks along: straight at the eyes. */
export function headingTo(from, to, turn) {
    return Math.hypot(to[0] - from[0], to[1] - from[1]) > 1e-9 ? turnTowards(from, to) : turn;
}
/** Points along a straight walk, every 0.25 m, both ends included. */
export function walkSamples(from, to) {
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    const n = Math.max(1, Math.ceil(length / MODEL_DISTANCE.sample - 1e-9));
    const points = [];
    for (let k = 0; k <= n; k++) {
        points.push([from[0] + ((to[0] - from[0]) * k) / n, from[1] + ((to[1] - from[1]) * k) / n]);
    }
    return points;
}
/**
 * Turns along a turn on the spot, the short way round, both ends included:
 * close enough that a corner `reach` metres out moves at most 0.25 m between two.
 */
export function turnSamples(from, to, reach) {
    const delta = shortestTurn(from, to);
    const arc = (Math.abs(delta) * Math.PI * Math.max(reach, 0)) / 180;
    const n = Math.max(1, Math.ceil(arc / MODEL_DISTANCE.sample - 1e-9));
    return Array.from({ length: n + 1 }, (_, k) => from + (delta * k) / n);
}
/** How far a footprint's farthest corner is from where the model stands: what a turn swings. */
export function footprintReach(footprint, pos) {
    return Math.max(...footprint.map(([x, z]) => Math.hypot(x - pos[0], z - pos[1])));
}
/**
 * The spec's table: a small model and a static exhibit keep 1.5 m from the eyes, calm or not; a big
 * one stays outside the stage's `modelClear` (3 m since ADR-084; 8.5 m on ADR-032's stage) and
 * `modelEyes` from the eyes (3 m; 6 m on ADR-032's), except while Spannend walks it to the pupil, when
 * its front keeps 2.5 m.
 */
export function distanceRule(standing, walkingToPupil = false, stage = STAGE) {
    if (standing !== 'big')
        return { eyes: MODEL_DISTANCE.smallEyes };
    if (walkingToPupil)
        return { eyes: MODEL_DISTANCE.spannendEyes };
    return { eyes: stage.modelEyes, apron: stage.modelClear };
}
/** How near a footprint comes to the eyes and the stage centre, and whether that keeps `rule`. */
export function keepsDistance(footprint, eye, rule) {
    const eyes = footprintDistance(footprint, eye);
    const centre = footprintDistance(footprint, [0, 0]);
    const ok = eyes >= rule.eyes - 1e-6 && (rule.apron === undefined || centre >= rule.apron - 1e-6);
    return { eyes, centre, ok };
}
//# sourceMappingURL=model-distance.js.map